import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import apiConfig from '../../config/apiConfig';
import { UserContext } from '../../context/UserContext';
import EmojiPicker from 'emoji-picker-react';
import './ManageHome.css';

const ManageHome = () => {
  const { user } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState('announcements');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '' });
  
  // Features state
  const [completedFeatures, setCompletedFeatures] = useState([]);
  const [upcomingFeatures, setUpcomingFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState({
    title: '',
    description: '',
    status: 'upcoming',
    emoji: '✨'
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editingFeatureId, setEditingFeatureId] = useState(null);
  
  // Roadmap state
  const [roadmapData, setRoadmapData] = useState([
    { id: 1, phase: 'Phase 1: Foundation', items: [], status: 'complete' },
    { id: 2, phase: 'Phase 2: Core Gameplay', items: [], status: 'current' },
    { id: 3, phase: 'Phase 3: Social Features', items: [], status: 'upcoming' },
    { id: 4, phase: 'Phase 4: Advanced Features', items: [], status: 'upcoming' }
  ]);
  const [newRoadmapPhase, setNewRoadmapPhase] = useState({ 
    phase: '', 
    items: '',
    status: 'upcoming'
  });
  const [editingPhaseId, setEditingPhaseId] = useState(null);

  // Check if user is admin
  const isAdmin = user && user.role >= 2;
  
  // Fetch all data on load
  useEffect(() => {
    if (isAdmin) {
      fetchAnnouncements();
      fetchFeatures();
      fetchRoadmap();
    }
  }, [isAdmin]);

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiConfig.announcements, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch announcements');
      }
      
      const data = await response.json();
      setAnnouncements(data);
      setError('');
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to load announcements: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch features
  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiConfig.features, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch features');
      }
      
      const data = await response.json();
      
      // Separate features by status
      const completed = data.filter(feature => feature.status === 'complete');
      const upcoming = data.filter(feature => feature.status === 'upcoming');
      
      setCompletedFeatures(completed);
      setUpcomingFeatures(upcoming);
      setError('');
    } catch (err) {
      console.error('Error fetching features:', err);
      setError('Failed to load features: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roadmap
  const fetchRoadmap = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiConfig.roadmap, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch roadmap');
      }
      
      const data = await response.json();
      setRoadmapData(data);
      setError('');
    } catch (err) {
      console.error('Error fetching roadmap:', err);
      setError('Failed to load roadmap: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create new announcement
  const createAnnouncement = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await fetch(apiConfig.announcements, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newAnnouncement)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create announcement');
      }
      
      setSuccess('Announcement created successfully!');
      setNewAnnouncement({ title: '', content: '' });
      fetchAnnouncements();
      setError('');
    } catch (err) {
      console.error('Error creating announcement:', err);
      setError('Failed to create announcement: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Delete an announcement
  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(apiConfig.deleteAnnouncement(id), {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete announcement');
      }
      
      setSuccess('Announcement deleted successfully!');
      fetchAnnouncements();
      setError('');
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError('Failed to delete announcement: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Create/update feature
  const saveFeature = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // If editing an existing feature, include the ID
      const featureData = editingFeatureId 
        ? { ...newFeature, id: editingFeatureId }
        : newFeature;
      
      const response = await fetch(apiConfig.features, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(featureData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save feature');
      }
      
      setSuccess('Feature saved successfully!');
      setNewFeature({
        title: '',
        description: '',
        status: 'upcoming',
        emoji: '✨'
      });
      setEditingFeatureId(null);
      fetchFeatures();
      setError('');
    } catch (err) {
      console.error('Error saving feature:', err);
      setError('Failed to save feature: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Delete feature
  const deleteFeature = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${apiConfig.features}/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete feature');
      }
      
      setSuccess('Feature deleted successfully!');
      fetchFeatures();
      setError('');
    } catch (err) {
      console.error('Error deleting feature:', err);
      setError('Failed to delete feature: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Edit feature (populate form with feature data)
  const editFeature = (feature) => {
    setNewFeature({
      title: feature.title,
      description: feature.description,
      status: feature.status,
      emoji: feature.emoji
    });
    setEditingFeatureId(feature.id);
    setActiveTab('addFeature');
  };

  // Save roadmap phase
  const saveRoadmapPhase = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Convert comma-separated items to array
      const formattedData = {
        ...newRoadmapPhase,
        items: newRoadmapPhase.items.split(',').map(item => item.trim())
      };
      
      // If editing, include the ID
      if (editingPhaseId) {
        formattedData.id = editingPhaseId;
      }
      
      const response = await fetch(apiConfig.roadmap, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formattedData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save roadmap phase');
      }
      
      setSuccess('Roadmap phase saved successfully!');
      setNewRoadmapPhase({ phase: '', items: '', status: 'upcoming' });
      setEditingPhaseId(null);
      fetchRoadmap();
      setError('');
    } catch (err) {
      console.error('Error saving roadmap phase:', err);
      setError('Failed to save roadmap phase: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Delete roadmap phase
  const deleteRoadmapPhase = async (id) => {
    if (!window.confirm('Are you sure you want to delete this roadmap phase?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${apiConfig.roadmap}/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete roadmap phase');
      }
      
      setSuccess('Roadmap phase deleted successfully!');
      fetchRoadmap();
      setError('');
    } catch (err) {
      console.error('Error deleting roadmap phase:', err);
      setError('Failed to delete roadmap phase: ' + err.message);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  // Edit roadmap phase
  const editRoadmapPhase = (phase) => {
    setNewRoadmapPhase({
      phase: phase.phase,
      items: phase.items.join(', '),
      status: phase.status
    });
    setEditingPhaseId(phase.id);
    setActiveTab('addRoadmap');
  };

  // Handle emoji selection
  const onEmojiClick = (emojiData) => {
    setNewFeature({
      ...newFeature,
      emoji: emojiData.emoji
    });
    setShowEmojiPicker(false);
  };

  // If not admin, redirect to home
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="page-content">
      <div className="admin-home-management">
        <h1>Manage Home Page Content</h1>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="admin-tabs">
          <button 
            className={activeTab === 'announcements' ? 'active' : ''}
            onClick={() => setActiveTab('announcements')}
          >
            Announcements
          </button>
          <button 
            className={activeTab === 'addAnnouncement' ? 'active' : ''}
            onClick={() => setActiveTab('addAnnouncement')}
          >
            Add Announcement
          </button>
          <button 
            className={activeTab === 'features' ? 'active' : ''}
            onClick={() => setActiveTab('features')}
          >
            Features
          </button>
          <button 
            className={activeTab === 'addFeature' ? 'active' : ''}
            onClick={() => {
              setEditingFeatureId(null);
              setNewFeature({
                title: '',
                description: '',
                status: 'upcoming',
                emoji: '✨'
              });
              setActiveTab('addFeature');
            }}
          >
            Add Feature
          </button>
          <button 
            className={activeTab === 'roadmap' ? 'active' : ''}
            onClick={() => setActiveTab('roadmap')}
          >
            Roadmap
          </button>
          <button 
            className={activeTab === 'addRoadmap' ? 'active' : ''}
            onClick={() => {
              setEditingPhaseId(null);
              setNewRoadmapPhase({ phase: '', items: '', status: 'upcoming' });
              setActiveTab('addRoadmap');
            }}
          >
            Add Roadmap Phase
          </button>
        </div>
        
        <div className="admin-content">
          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <>
              <h2>Current Announcements</h2>
              {loading && announcements.length === 0 && <div className="loading">Loading announcements...</div>}
              
              {announcements.length > 0 ? (
                <div className="announcements-list">
                  {announcements.map(announcement => (
                    <div key={announcement.id} className="announcement-item">
                      <div className="announcement-header">
                        <h3>{announcement.title}</h3>
                        <div className="announcement-date">{new Date(announcement.date).toLocaleDateString()}</div>
                      </div>
                      <p>{announcement.content}</p>
                      <button 
                        className="admin-button delete-button"
                        onClick={() => deleteAnnouncement(announcement.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : !loading && (
                <p>No announcements yet. Create one to get started!</p>
              )}
            </>
          )}
          
          {/* Add Announcement Tab */}
          {activeTab === 'addAnnouncement' && (
            <>
              <h2>Add New Announcement</h2>
              <form onSubmit={createAnnouncement}>
                <div className="form-group">
                  <label htmlFor="announcement-title">Title</label>
                  <input
                    id="announcement-title"
                    type="text"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="announcement-content">Content</label>
                  <textarea
                    id="announcement-content"
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="admin-button"
                  disabled={loading || !newAnnouncement.title || !newAnnouncement.content}
                >
                  {loading ? 'Saving...' : 'Save Announcement'}
                </button>
              </form>
            </>
          )}
          
          {/* Features Tab */}
          {activeTab === 'features' && (
            <>
              <h2>Current Features</h2>
              {loading && completedFeatures.length === 0 && upcomingFeatures.length === 0 && (
                <div className="loading">Loading features...</div>
              )}
              
              <div className="features-section">
                <h3>Completed Features</h3>
                {completedFeatures.length > 0 ? (
                  <div className="features-list">
                    {completedFeatures.map(feature => (
                      <div key={feature.id} className="feature-item">
                        <div className="feature-header">
                          <div className="feature-emoji">{feature.emoji}</div>
                          <h4>{feature.title}</h4>
                        </div>
                        <p>{feature.description}</p>
                        <div className="feature-actions">
                          <button 
                            className="admin-button edit-button"
                            onClick={() => editFeature(feature)}
                          >
                            Edit
                          </button>
                          <button 
                            className="admin-button delete-button"
                            onClick={() => deleteFeature(feature.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !loading && (
                  <p>No completed features yet.</p>
                )}
                
                <h3>Upcoming Features</h3>
                {upcomingFeatures.length > 0 ? (
                  <div className="features-list">
                    {upcomingFeatures.map(feature => (
                      <div key={feature.id} className="feature-item">
                        <div className="feature-header">
                          <div className="feature-emoji">{feature.emoji}</div>
                          <h4>{feature.title}</h4>
                        </div>
                        <p>{feature.description}</p>
                        <div className="feature-actions">
                          <button 
                            className="admin-button edit-button"
                            onClick={() => editFeature(feature)}
                          >
                            Edit
                          </button>
                          <button 
                            className="admin-button delete-button"
                            onClick={() => deleteFeature(feature.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !loading && (
                  <p>No upcoming features yet.</p>
                )}
              </div>
            </>
          )}
          
          {/* Add Feature Tab */}
          {activeTab === 'addFeature' && (
            <>
              <h2>{editingFeatureId ? 'Edit Feature' : 'Add New Feature'}</h2>
              <form onSubmit={saveFeature}>
                <div className="form-group emoji-input-container">
                  <label>Emoji Icon</label>
                  <div className="emoji-selector">
                    <button 
                      type="button" 
                      className="emoji-button" 
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      {newFeature.emoji || '😀'}
                    </button>
                    
                    {showEmojiPicker && (
                      <div className="emoji-picker-container">
                        <EmojiPicker onEmojiClick={onEmojiClick} />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="feature-title">Title</label>
                  <input
                    id="feature-title"
                    type="text"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature({...newFeature, title: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="feature-description">Description</label>
                  <textarea
                    id="feature-description"
                    value={newFeature.description}
                    onChange={(e) => setNewFeature({...newFeature, description: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Status</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="feature-status"
                        value="complete"
                        checked={newFeature.status === 'complete'}
                        onChange={() => setNewFeature({...newFeature, status: 'complete'})}
                      />
                      Complete
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="feature-status"
                        value="upcoming"
                        checked={newFeature.status === 'upcoming'}
                        onChange={() => setNewFeature({...newFeature, status: 'upcoming'})}
                      />
                      Coming Soon
                    </label>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="admin-button"
                  disabled={loading || !newFeature.title || !newFeature.description}
                >
                  {loading ? 'Saving...' : editingFeatureId ? 'Update Feature' : 'Add Feature'}
                </button>
              </form>
            </>
          )}
          
          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <>
              <h2>Development Roadmap</h2>
              {loading && roadmapData.length === 0 && (
                <div className="loading">Loading roadmap...</div>
              )}
              
              {roadmapData.length > 0 ? (
                <div className="roadmap-list">
                  {roadmapData.map(phase => (
                    <div key={phase.id} className="roadmap-item">
                      <div className="roadmap-header">
                        <h3>{phase.phase}</h3>
                        <div className={`roadmap-status ${phase.status}`}>{phase.status}</div>
                      </div>
                      <ul className="roadmap-items">
                        {phase.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                      <div className="roadmap-actions">
                        <button 
                          className="admin-button edit-button"
                          onClick={() => editRoadmapPhase(phase)}
                        >
                          Edit
                        </button>
                        <button 
                          className="admin-button delete-button"
                          onClick={() => deleteRoadmapPhase(phase.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !loading && (
                <p>No roadmap data yet.</p>
              )}
            </>
          )}
          
          {/* Add Roadmap Phase Tab */}
          {activeTab === 'addRoadmap' && (
            <>
              <h2>{editingPhaseId ? 'Edit Roadmap Phase' : 'Add Roadmap Phase'}</h2>
              <form onSubmit={saveRoadmapPhase}>
                <div className="form-group">
                  <label htmlFor="roadmap-phase">Phase Title</label>
                  <input
                    id="roadmap-phase"
                    type="text"
                    value={newRoadmapPhase.phase}
                    onChange={(e) => setNewRoadmapPhase({...newRoadmapPhase, phase: e.target.value})}
                    placeholder="e.g. Phase 1: Foundation"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="roadmap-items">Items (comma separated)</label>
                  <textarea
                    id="roadmap-items"
                    value={newRoadmapPhase.items}
                    onChange={(e) => setNewRoadmapPhase({...newRoadmapPhase, items: e.target.value})}
                    placeholder="User registration, Character creation, Chat system"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Phase Status</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="roadmap-status"
                        value="complete"
                        checked={newRoadmapPhase.status === 'complete'}
                        onChange={() => setNewRoadmapPhase({...newRoadmapPhase, status: 'complete'})}
                      />
                      Complete
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="roadmap-status"
                        value="current"
                        checked={newRoadmapPhase.status === 'current'}
                        onChange={() => setNewRoadmapPhase({...newRoadmapPhase, status: 'current'})}
                      />
                      Current
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="roadmap-status"
                        value="upcoming"
                        checked={newRoadmapPhase.status === 'upcoming'}
                        onChange={() => setNewRoadmapPhase({...newRoadmapPhase, status: 'upcoming'})}
                      />
                      Upcoming
                    </label>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="admin-button"
                  disabled={loading || !newRoadmapPhase.phase || !newRoadmapPhase.items}
                >
                  {loading ? 'Saving...' : editingPhaseId ? 'Update Phase' : 'Add Phase'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageHome;